// src/components/dashboard/ShiftSwapPage.jsx
import React, { useState } from "react";
import { useAuth, useShiftSwaps, EmployeeProvider } from "@/context";
import SwapForm from "./SwapForm";
import api from "@/config/api";
import ReasonModal from "./ReasonModal";
import Swal from "sweetalert2";
import {
  TrashIcon,
  PauseCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  MinusCircleIcon,
  ClockIcon,
  CheckIcon,
  XMarkIcon,
  StopCircleIcon,
} from "@heroicons/react/24/solid";

function getErrMessage(err, fb = "Terjadi kesalahan") {
  return err?.response?.data?.message || err?.message || fb;
}

/** Normalisasi status supaya konsisten */
const normStatus = (s) => {
  s = String(s || "").toLowerCase();
  if (s === "cancel" || s === "cancelled") return "canceled";
  if (s === "decline") return "declined";
  return s;
};

/** Badge status dengan ikon (mirip pengajuan cuti/izin) */
function StatusBadge({ status }) {
  const s = normStatus(status);

  const color = {
    pending: "bg-yellow-100 text-yellow-800 ring-yellow-200",
    approved: "bg-green-100 text-green-700 ring-green-200",
    accepted: "bg-blue-100 text-blue-700 ring-blue-200",
    rejected: "bg-red-100 text-red-700 ring-red-200",
    canceled: "bg-gray-100 text-gray-700 ring-gray-200",
    waiting: "bg-gray-100 text-gray-700 ring-gray-200",
    declined: "bg-red-100 text-red-700 ring-red-200",
    expired: "bg-orange-100 text-orange-700 ring-orange-200",
  }[s] || "bg-gray-100 text-gray-700 ring-gray-200";

  const label = {
    pending: "Pending",
    approved: "Approved",
    accepted: "Accepted",
    rejected: "Rejected",
    canceled: "Canceled",
    waiting: "Waiting",
    declined: "Declined",
    expired: "Expired",
  }[s] || (status || "-");

  const icon = {
    pending: <PauseCircleIcon className="w-4 h-4" />,
    approved: <CheckCircleIcon className="w-4 h-4" />,
    accepted: <CheckCircleIcon className="w-4 h-4" />,
    rejected: <XCircleIcon className="w-4 h-4" />,
    canceled: <MinusCircleIcon className="w-4 h-4" />,
    waiting: <PauseCircleIcon className="w-4 h-4" />,
    declined: <XCircleIcon className="w-4 h-4" />,
    expired: <ClockIcon className="w-4 h-4" />,
  }[s] || <MinusCircleIcon className="w-4 h-4" />;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium ring-1 ${color}`}>
      {icon}
      {label}
    </span>
  );
}

/* ===== Helpers UI ===== */
const pick = (...vals) => vals.find(v => v !== undefined && v !== null && String(v).trim() !== "");

const escapeHtml = (str = "") =>
  String(str).replace(/&/g,"&amp;").replace(/</g,"&lt;")
             .replace(/>/g,"&gt;").replace(/"/g,"&quot;").replace(/'/g,"&#039;");

const showFullReason = (title, reason) => {
  if (!reason) return;
  Swal.fire({
    icon: "info",
    title,
    html: `<div style="text-align:left;white-space:pre-wrap">${escapeHtml(reason)}</div>`,
    confirmButtonText: "Tutup",
  });
};

// maxWidthClass = batas lebar supaya truncate bekerja di <table>
const ReasonInline = ({ label, reason, color = "text-gray-600", limit = 20, maxWidthClass = "max-w-[240px]" }) => {
  if (!reason) return null;
  const s = String(reason).trim();
  const truncated = s.length > limit;
  const short = truncated ? s.slice(0, limit - 1) + "…" : s;

  return (
    <div className={`text-xs ${color} ${maxWidthClass} truncate whitespace-nowrap`}>
      {label}:{" "}
      {truncated ? (
        <span
          role="button"
          onClick={() => showFullReason(label, reason)}
          className="underline decoration-dotted underline-offset-2 hover:decoration-solid cursor-pointer align-middle"
        >
          {short}
        </span>
      ) : (
        short
      )}
    </div>
  );
};

/** Approver display sederhana (tanpa inferApproverRoleFromRequester) */
function approverDisplay(row) {
  // Prioritas role dari backend bila ada
  const role =
    row?.rejected_by_role ||
    row?.approved_by_role ||
    row?.approvedBy?.role ||
    row?.approver?.role;

  if (role) return String(role).toUpperCase();

  // Fallback: infer dari role requester
  const reqRole = String(row?.requester?.role || row?.employee?.role || "").toLowerCase();
  if (reqRole === "employee" || reqRole === "acos") return "COS";
  if (reqRole === "cos") return "ADMIN";

  return "APPROVER";
}
/* ===== End Helpers UI ===== */

export default function ShiftSwapPage() {
  const { user } = useAuth() || {};
  const {
    list = [],
    loadingData,
    error: contextError,
    approveSwap,
    fetchList,
    requesterCancel,
  } = useShiftSwaps();

  const [localError, setLocalError] = useState("");
  const [loadingButtons, setLoadingButtons] = useState({});
  const [shadowPartnerStatus, setShadowPartnerStatus] = useState({});

  // state ReasonModal
  const [reasonModal, setReasonModal] = useState({
    open: false,
    id: null,
    mode: null, // 'cosReject' | 'partnerDecline' | 'requesterCancel'
    submitting: false,
  });

  const setBusy = (id, key, value) =>
    setLoadingButtons((p) => ({ ...p, [id]: { ...(p[id] || {}), [key]: value } }));

  const isAdmin = user?.role === "admin";
  const isCOS = user?.role === "cos";

  // Siapa boleh approve tukar shift
  const canApproveSwap = (row, effectivePartnerStatus) => {
    // Harus masih pending & partner sudah accepted
    if (normStatus(row.status) !== "pending") return false;
    if (normStatus(effectivePartnerStatus) !== "accepted") return false;

    // Admin bisa approve semua
    if (isAdmin) return true;

    // COS: boleh approve Employee/ACOS atau dirinya sendiri
    if (isCOS) {
      const reqRole = String(row?.requester?.role || row?.employee?.role || "").toLowerCase();
      const isReqEmployeeOrAcos = reqRole === "employee" || reqRole === "acos";
      const isSelf = Number(row?.requester?.id || 0) === Number(user?.id || -1);
      return isReqEmployeeOrAcos || isSelf;
    }

    // selain itu: tidak boleh
    return false;
  };

  // === COS approve langsung
  const handleCosApprove = async (id) => {
    setLocalError("");
    setBusy(id, "approve", true);
    try {
      await approveSwap(id, "approve");
      if (fetchList) await fetchList();
    } catch (err) {
      setLocalError(getErrMessage(err));
    } finally {
      setBusy(id, "approve", false);
    }
  };

  const openCosReject = (id) => setReasonModal({ open: true, id, mode: "cosReject", submitting: false });

  // === Partner accept/decline
  const handlePartnerAccept = async (id) => {
    setLocalError("");
    setBusy(id, "accept", true);
    setShadowPartnerStatus((p) => ({ ...p, [id]: "accepted" })); // optimistic
    try {
      await api.post(`/shift-swaps/${id}/partner?action=accept`, { action: "accept" });
    } catch (err) {
      const msg = getErrMessage(err, "");
      const already = err?.response?.status === 422 && /diproses/i.test(msg || "");
      if (!already) {
        setShadowPartnerStatus((p) => {
          const c = { ...p };
          delete c[id];
          return c;
        });
      }
    } finally {
      setBusy(id, "accept", false);
      if (fetchList) await fetchList();
    }
  };

  const openPartnerDecline = (id) =>
    setReasonModal({ open: true, id, mode: "partnerDecline", submitting: false });

  // === Requester cancel
  const openRequesterCancel = (id) =>
    setReasonModal({ open: true, id, mode: "requesterCancel", submitting: false });

  // === Submit modal alasan
  const submitReason = async (note) => {
    const { id, mode } = reasonModal;
    setReasonModal((m) => ({ ...m, submitting: true }));
    setLocalError("");

    try {
      if (mode === "cosReject") {
        await approveSwap(id, "reject", { note });
      } else if (mode === "partnerDecline") {
        await api.post(`/shift-swaps/${id}/partner?action=decline`, { action: "decline", note });
      } else if (mode === "requesterCancel") {
        await requesterCancel(id, note);
      }
      if (fetchList) await fetchList();
      setReasonModal({ open: false, id: null, mode: null, submitting: false });
    } catch (err) {
      setLocalError(getErrMessage(err));
      setReasonModal((m) => ({ ...m, submitting: false }));
    }
  };

  const cancelModal = () => setReasonModal({ open: false, id: null, mode: null, submitting: false });

  const [deletingAll, setDeletingAll] = useState(false);

  // role yang boleh hapus
  const canDelete = () => user?.role === "admin";
  const canDeleteAll = user?.role === "admin";

  // SweetAlert2 konfirmasi
  const confirmDeleteSingle = async (names = "") => {
    const res = await Swal.fire({
      icon: "warning",
      title: "Yakin ingin menghapus?",
      html: names
        ? `Data tukar shift <b>${names}</b> akan dihapus permanen.`
        : "Data ini akan dihapus permanen.",
      showCancelButton: true,
      confirmButtonText: "Ya, hapus!",
      cancelButtonText: "Batal",
      reverseButtons: true,
      focusCancel: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
    });
    return res.isConfirmed;
  };

  const confirmDeleteAll = async () => {
    const res = await Swal.fire({
      icon: "warning",
      title: "Hapus SEMUA data tukar shift?",
      html: "Semua data tukar shift akan dihapus permanen dan tidak bisa dikembalikan.",
      showCancelButton: true,
      confirmButtonText: "Ya, hapus semua!",
      cancelButtonText: "Batal",
      reverseButtons: true,
      focusCancel: true,
      confirmButtonColor: "#991b1b",
      cancelButtonColor: "#6b7280",
    });
    return res.isConfirmed;
  };

  // handler hapus per-data
  const handleDelete = async (id, requesterName, partnerName) => {
    const agreed = await confirmDeleteSingle(
      requesterName && partnerName ? `"${requesterName}" ⇄ "${partnerName}"` : ""
    );
    if (!agreed) return;

    setBusy(id, "delete", true);
    setLocalError("");
    try {
      await api.delete(`/shift-swaps/${id}`);
      if (fetchList) await fetchList();
    } catch (err) {
      setLocalError(getErrMessage(err));
    } finally {
      setBusy(id, "delete", false);
    }
  };

  // handler hapus semua data
  const handleDeleteAll = async () => {
    const agreed = await confirmDeleteAll();
    if (!agreed) return;

    setDeletingAll(true);
    setLocalError("");
    try {
      await api.delete(`/shift-swaps`);
      if (fetchList) await fetchList();
    } catch (err) {
      setLocalError(getErrMessage(err));
    } finally {
      setDeletingAll(false);
    }
  };

  // --- urutan kelompok seperti di izin/cuti ---
const STATUS_ORDER = { pending: 1, approved: 2, rejected: 3, canceled: 4 };

// partner: accepted di atas, pending/waiting di tengah, declined/canceled di bawah
const PARTNER_ORDER = { accepted: 1, pending: 2, waiting: 2, declined: 3, rejected: 3, canceled: 4, "": 5 };

const toKey = (v) => String(v || "").toLowerCase();

const cmpSwap = (a, b) => {
  // status utama
  const sa = STATUS_ORDER[toKey(a.status)] ?? 99;
  const sb = STATUS_ORDER[toKey(b.status)] ?? 99;
  if (sa !== sb) return sa - sb;

  // status partner (pakai shadow kalau ada)
  const pa = PARTNER_ORDER[toKey(shadowPartnerStatus[a.id] || a.partner_status)] ?? 99;
  const pb = PARTNER_ORDER[toKey(shadowPartnerStatus[b.id] || b.partner_status)] ?? 99;
  if (pa !== pb) return pa - pb;

  // tanggal terbaru dulu
  const da = new Date(a.date || "1970-01-01").getTime();
  const db = new Date(b.date || "1970-01-01").getTime();
  return db - da;
};

  const btnBase = "shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md";
  const reasonMaxW = "max-w-[520px] sm:max-w-[600px]";

  return (
    <div className="p-6">
      {/* Form tukar disembunyikan untuk admin & ac */}
      {!["admin", "ac"].includes(user?.role) && (
        <EmployeeProvider>
          <div className="mb-6 max-w-4xl w-full mx-auto"> 
            <SwapForm />
          </div>
        </EmployeeProvider>
      )}

      {loadingData && <p className="text-gray-500 mb-2">Memuat data…</p>}
      {(contextError || localError) && (
        <p className="text-red-500 mb-3">{contextError || localError}</p>
      )}

      {/* Tombol Hapus Semua — admin only */}
      {canDeleteAll && (
        <div className="mt-2 mb-5 pt-2 flex justify-end">
          <button
            onClick={handleDeleteAll}
            disabled={deletingAll || loadingData}
            className={`inline-flex items-center gap-1 px-3 py-2 rounded text-white ${
              deletingAll ? "bg-red-400 cursor-not-allowed" : "bg-red-800 hover:bg-red-900"
            }`}
            title="Hapus semua data tukar shift"
          >
            <TrashIcon className="w-5 h-5" />
            {deletingAll ? "Menghapus..." : "Hapus Semua Data"}
          </button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full border-collapse border rounded-md overflow-hidden">
          <thead className="bg-gray-100">
            <tr>
              <th className="border p-2 text-left">Pemohon</th>
              <th className="border p-2 text-left">Partner</th>
              <th className="border p-2 text-center">Tanggal</th>
              <th className="border p-2 text-left">Status</th>
              <th className="border p-2 text-center whitespace-nowrap">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loadingData ? (
              <tr>
                <td colSpan={5} className="text-center p-4 text-gray-500">Loading...</td>
              </tr>
            ) : list.length > 0 ? (
                [...list].sort(cmpSwap).map((r) => {
                  const btn = loadingButtons[r.id] || {};

                  const s = normStatus(r.status);
                  const isPending  = s === "pending";
                  const isApproved = s === "approved";
                  const isRejected = s === "rejected";
                  const isCanceled = s === "canceled";

                  const rawPartnerStatus = normStatus(shadowPartnerStatus[r.id] || r.partner_status || "waiting");
                  const displayPartnerStatus = isCanceled ? "canceled" : rawPartnerStatus;

                const isRequester = user?.id === r.requester?.id;

                const canPartnerAct =
                  user?.id === r.partner?.id &&
                  s === "pending" &&
                  rawPartnerStatus === "waiting";

                const canApproverAct = canApproveSwap(r, rawPartnerStatus);

                const canRequesterCancel =
                  isRequester &&
                  s === "pending" &&
                  rawPartnerStatus === "waiting";

                // hanya admin yg boleh hapus
                const canDeleteRow = canDelete();

                const hasActions =
                  canPartnerAct || canApproverAct || canRequesterCancel || canDeleteRow;

                const reqShiftName = r.requester_shift?.name || r.requesterShift?.name;
                const reqShiftCode = r.requester_shift?.code || r.requesterShift?.code;
                const parShiftName = r.partner_shift?.name || r.partnerShift?.name;
                const parShiftCode = r.partner_shift?.code || r.partnerShift?.code;

                return (
                  <tr key={r.id} className="align-top">
                    <td className="border p-2">
                      <div className="font-medium">{r.requester?.name || "-"}</div>
                      {(reqShiftName || reqShiftCode) && (
                        <div className="text-xs text-gray-500">
                          {reqShiftName} {reqShiftCode ? `(${reqShiftCode})` : ""}
                        </div>
                      )}
                    </td>

                    <td className="border p-2">
                      <div className="font-medium">{r.partner?.name || "-"}</div>
                      {(parShiftName || parShiftCode) && (
                        <div className="text-xs text-gray-500">
                          {parShiftName} {parShiftCode ? `(${parShiftCode})` : ""}
                        </div>
                      )}
                    </td>

                    <td className="border p-2 text-center">{r.date || "-"}</td>

                    {/* STATUS */}
                    <td className="border px-2 py-1.5">
                      <div className="flex flex-col items-start gap-1">
                        {/* Baris 1: badge utama + partner (INLINE, satu baris) */}
                        <div className="flex flex-wrap items-center gap-2">
                          <StatusBadge status={s} />

                          {displayPartnerStatus && (
                            <span className="inline-flex items-center gap-1">
                              <span className="text-[11px] leading-none text-gray-500">partner:</span>
                              <StatusBadge status={displayPartnerStatus} />
                            </span>
                          )}
                        </div>

                        {isPending && (
                          <ReasonInline
                            label={`Tukar Shift ${r.requester?.name} Dengan ${r.partner?.name}`}
                            reason={
                              displayPartnerStatus === "accepted"
                                ? `Disetujui oleh partner (${r.partner?.name}). Menunggu persetujuan ${approverDisplay(r)}`
                                : displayPartnerStatus === "pending" || displayPartnerStatus === "waiting"
                                  ? `Menunggu konfirmasi  oleh partner (${r.partner?.name})`
                                  : displayPartnerStatus === "declined" || displayPartnerStatus === "rejected"
                                    ? `Partner menolak: ${r.partner_note || "-"}`
                                    : `Menunggu persetujuan ${approverDisplay(r)}`
                            }
                            color={
                              displayPartnerStatus === "declined" || displayPartnerStatus === "rejected"
                                ? "text-red-600"
                                : "text-yellow-600"
                            }
                            limit={10}
                            maxWidthClass={reasonMaxW}
                          />
                        )}

                        {isApproved && (
                          <ReasonInline
                            label={`Tukar Shift ${r.requester?.name} Dengan ${r.partner?.name}`}
                            reason={
                              displayPartnerStatus === "accepted"
                                ? `Disetujui oleh partner (${r.partner?.name}) dan ${approverDisplay(r)}. Efektif untuk ${r.date || "tanggal terkait"}`
                                : `Disetujui oleh ${approverDisplay(r)}`
                            }
                            color="text-green-600"
                            limit={11}
                            maxWidthClass={reasonMaxW}
                          />
                        )}

                        {isRejected && (
                          <ReasonInline
                            label={`Tukar Shift ${r.requester?.name} Dengan ${r.partner?.name}`}
                            reason={
                              displayPartnerStatus === "declined"
                                ? `Ditolak oleh Partner (${r.partner?.name}) : ${r.partner_note}`
                                : `Patner (${r.partner?.name}) telah menyetujui, namun ditolak oleh ${approverDisplay(r)} : ${r.reject_reason}`
                            }
                            color="text-red-600"
                            limit={8}
                            maxWidthClass={reasonMaxW}
                          />
                        )}

                        {isCanceled && (
                          <ReasonInline
                            label={`Tukar Shift ${r.requester?.name} Dengan ${r.partner?.name}`}
                            reason={`Dibatalkan oleh Pemohon (${r.requester?.name}) : ${r.cancel_reason}`}
                            color="text-gray-600"
                            limit={12}
                            maxWidthClass={reasonMaxW}
                          />
                        )}
                      </div>
                    </td>

                    {/* AKSI */}
                    <td className="border p-2 text-center">
                      <div className="flex items-center justify-center gap-2 flex-nowrap whitespace-nowrap">
                        {canPartnerAct && (
                          <>
                            <button
                              onClick={() => handlePartnerAccept(r.id)}
                              className={`${btnBase} bg-blue-600 text-white hover:bg-blue-700`}
                              disabled={btn.accept}
                              title="Terima permintaan tukar"
                            >
                              <CheckIcon className="w-4 h-4" />
                              {btn.accept ? "Memproses…" : "Terima"}
                            </button>
                            <button
                              onClick={() => openPartnerDecline(r.id)}
                              className={`${btnBase} bg-red-600 text-white hover:bg-red-700`}
                              disabled={btn.decline}
                              title="Tolak permintaan tukar"
                            >
                              <XMarkIcon className="w-4 h-4" />
                              Tolak
                            </button>
                          </>
                        )}

                        {canApproverAct && (
                          <>
                            <button
                              onClick={() => handleCosApprove(r.id)}
                              className={`${btnBase} bg-green-600 text-white hover:bg-green-700`}
                              disabled={btn.approve}
                              title="Setujui tukar shift"
                            >
                              <CheckIcon className="w-4 h-4" />
                              {btn.approve ? "Memproses…" : "Approve"}
                            </button>
                            <button
                              onClick={() => openCosReject(r.id)}
                              className={`${btnBase} bg-gray-700 text-white hover:bg-gray-800`}
                              disabled={btn.reject}
                              title="Tolak tukar shift"
                            >
                              <XMarkIcon className="w-4 h-4" />
                              Reject
                            </button>
                          </>
                        )}

                        {canRequesterCancel && (
                          <button
                            onClick={() => openRequesterCancel(r.id)}
                            className={`${btnBase} bg-orange-600 text-white hover:bg-orange-700`}
                            disabled={btn.cancel}
                            title="Batalkan pengajuan"
                          >
                            <StopCircleIcon className="w-4 h-4" />
                            Batalkan
                          </button>
                        )}

                        {canDeleteRow && (
                          <button
                            onClick={() => handleDelete(r.id, r.requester?.name, r.partner?.name)}
                            className={`${btnBase} bg-red-600 text-white hover:bg-red-700`}
                            disabled={btn.delete}
                            title="Hapus data ini"
                          >
                            <TrashIcon className="w-4 h-4" />
                            {btn.delete ? "Menghapus..." : "Hapus"}
                          </button>
                        )}
                      </div>

                      {/* jangan tampilkan '-' jika sudah ada aksi */}
                      {!hasActions ? <span className="text-gray-400">-</span> : null}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={5} className="text-center p-4 text-gray-400">Tidak ada data</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal alasan */}
      <ReasonModal
        open={reasonModal.open}
        submitting={reasonModal.submitting}
        onClose={() => (reasonModal.submitting ? null : cancelModal())}
        onSubmit={submitReason}
        title={
          reasonModal.mode === "cosReject"
            ? "Tolak Pengajuan"
            : reasonModal.mode === "partnerDecline"
            ? "Tolak Pengajuan"
            : "Batalkan Pengajuan"
        }
        placeholder={
          reasonModal.mode === "requesterCancel"
            ? "Tulis alasan pembatalan…"
            : "Tulis alasan penolakan…"
        }
        confirmText={reasonModal.mode === "requesterCancel" ? "Batalkan" : "Kirim"}
      />
    </div>
  );
}
